import flow from 'lodash/fp/flow';
import reduce from 'lodash/fp/reduce';
import filter from 'lodash/fp/filter';

import { createSelector } from 'reselect';
import { GlobalState } from '../../../types.global';
import { AdTile, GamesPromo, GameTile, Tile } from './types';
import { getDaysOfUsage } from '../../story/slice/utils';

import { selectIsUserRegular } from '../../../store/selectors';
import { ZeroparkBrand } from '../../searchForm/slice/types';
import { withHttp } from '../../searchForm/slice/utils';
import { getFaviconUrl } from '../../searchForm/slice/selectors/suggestions/utils';
import { FeatureFlags } from '../../user/slice/types';

export const selectDrawAttentionToGames = ({
  user,
  tiles,
}: GlobalState): boolean => {
  if (
    user.ntpVisitCounter === 3 ||
    (!tiles.gamesPromo.gamesClicked &&
      getDaysOfUsage(user.installationDate) > 10)
  )
    return false;
  return user.ntpDayVisitCounter === 3;
};

const DEFAULT_FEATURE_FLAGS = {};
const getFeatureFlags = (state): FeatureFlags => {
  try {
    return state.user.experiment.value.scenario.featureFlags;
  } catch (err) {
    return DEFAULT_FEATURE_FLAGS;
  }
};

export const selectAdTilesList = (state: GlobalState) => state.tiles.adTiles;

export const selectAdTiles = createSelector(
  selectIsUserRegular,
  getFeatureFlags,
  selectAdTilesList,
  (state) => state.user.utmCampaign,
  (state) => state.search.$entities,
  (state) => state.user.installationDate,
  (isRegular, featureFlags, adTiles, isPayedUser, $entities, installationDate) => {

    if (getDaysOfUsage(installationDate) < 3 && !isPayedUser) {
      return [];
    }

    if (!adTiles.valueMap.length) {
      return [];
    }

    const now = Date.now();

    try {
      const filteredAdTiles = adTiles.value.filter((tile) => {
        const clickData = adTiles.clickTracking?.[tile.click_url] || {};

        if (clickData.hiddenUntil && clickData.hiddenUntil <= now) {
          // clickData.count = 0;
          // clickData.hiddenUntil = null;
          delete adTiles.clickTracking[tile.click_url];
        }

        return !(clickData.hiddenUntil && clickData.hiddenUntil > now);
      });

      const adsCount = adTiles.valueMap.filter(Boolean).length;
      const baseAdTiles = adTiles.valueMap
        .map((bool, index) => {
          if (!bool) return null;
          return filteredAdTiles[index];
        })
        .filter(Boolean);

      if (!featureFlags.ZP_TILES) {
        return baseAdTiles;
      }

      const history = $entities?.history || [];

      const { matching, remaining } = flow(
        filter(
          ({ brand }) => !adTiles.removedUrls.includes(brand.url.toLowerCase())
        ),
        reduce(
          (acc, { brand }: ZeroparkBrand) => {
            const index = history.findIndex((page) =>
              page.url.includes(brand.url)
            );
            if (index === -1) {
              acc.remaining.push(brand);
            } else {
              acc.matching[index] = brand;
            }

            return acc;
          },
          { matching: [], remaining: [] }
        ),
        (acc) => ({
          ...acc,
          matching: acc.matching.filter(Boolean),
        })
      )($entities?.zp?.brands || []);

      return new Array(adsCount)
        .fill('')
        .map(() => {
          const adTile = baseAdTiles[0];

          const createBrandTile = (brand) => {
            if (!brand) return null;

            const fullUrl = withHttp(brand.url);
            return {
              id: brand.url,
              image_url: getFaviconUrl(fullUrl, '48'),
              impression_url: null,
              name: brand.name,
              title: brand.name,
              type: 'adTiles',
              provider: 'zeropark',
              url: $entities.zp.clickUrl
                .replace(/\${brand.url}/g, brand.url)
                .replace(/\${brand.name}/g, brand.name),
            };
          };

          try {
            if (
              adTile &&
              history.find(({ url }) =>
                (url || '').includes(adTile.name.toLowerCase())
              )
            ) {
              return baseAdTiles.shift();
            }

            if (matching.length) {
              const brand = matching.shift();
              return createBrandTile(brand);
            }
          } catch (err) {
            console.error('selectAdTiles', err);
          }

          return baseAdTiles.shift() || createBrandTile(remaining.shift());
        })
        .filter(Boolean)
        .filter(
          (value, index, array) =>
            array.findIndex(({ id }) => id === value.id) === index
        );
    } catch (err) {
      console.error('selectAdTiles', err);
    }

    return [];
  }
);

export const selectShortcuts = ({ tiles }: GlobalState): Tile[] =>
  tiles.shortcuts.value;

export const selectGames = ({ tiles }: GlobalState): GameTile =>
  tiles.games.value;

export const selectGamesPromo = ({ tiles }: GlobalState): GamesPromo =>
  tiles.gamesPromo;

export const selectGamesPromoActive = createSelector(
  selectGamesPromo,
  (state) => state.user.ntpVisitCounter,
  (gamesPromo, counter): { active: boolean; fresh: boolean } => ({
    active: !gamesPromo.closedAt && !!gamesPromo.shownAtNtpVisitCounter,
    fresh: gamesPromo.shownAtNtpVisitCounter === counter,
  })
);

export const selectTopSites = createSelector(
  (state: GlobalState) => state.tiles.topSites.value,
  (sites): Tile[] => sites.filter(({ removed }) => !removed)
);

export const selectPagedTiles = createSelector(
  (state) => state.user.shells.boosters,
  selectAdTiles,
  selectShortcuts,
  selectTopSites,
  selectGames,

  (boosters, adTiles, shortcuts, topSites, games): Tile[][] | AdTile[][] => {
    const PAGE_SIZE = 10;
    const tiles = [...adTiles, games, ...shortcuts, ...topSites].filter(
      Boolean
    );

    // console.log('tiles', tiles);

    const numOfPages = Math.ceil(tiles.length / PAGE_SIZE);
    const lastPageRemainder = tiles.length % PAGE_SIZE;
    const currPage = (i): number => Math.floor(i / PAGE_SIZE);

    const pagedTiles = tiles.reduce(
      (acc, val, i) => acc[currPage(i)].push(val) && acc,
      Array(numOfPages)
        .fill(null)
        .map((_, i) =>
          Array(i === numOfPages - 1 ? lastPageRemainder : PAGE_SIZE)
        )
    );

    pagedTiles['-1'] = [...Array(10)];

    return pagedTiles;
  }
);
